import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    IconButton,
    Modal,
    TextField,
    Typography,
} from '@mui/material';

import { Icon } from '../shared/Icon';
import { LanguageType } from '@lifeserver/shared';

import styles from './PortalTranslations.module.scss';

export type MetaForm = {
    characterLimit: number;
    notes: string;
};

type Props = {
    open: boolean;
    initialLocalizationId: string;
    initialMetaForm: MetaForm;
    initialLocalizationForm: Record<string, string>;
    onClose: () => void;
    onSave: (result: {
        localizationId: string;
        metaForm: MetaForm;
        localizationForm: Record<string, string>;
    }) => void;
};

export const PortalTranslationsEditModal: React.FC<Props> = ({
    open,
    initialLocalizationId,
    initialMetaForm,
    initialLocalizationForm,
    onClose,
    onSave,
}) => {
    const [localizationId, setLocalizationId] = useState(initialLocalizationId);
    const [metaForm, setMetaForm] = useState(initialMetaForm);
    const [localizationForm, setLocalizationForm] = useState(
        initialLocalizationForm,
    );
    useEffect(() => {
        setLocalizationId(initialLocalizationId);
        setMetaForm(initialMetaForm);
        setLocalizationForm(initialLocalizationForm);
    }, [initialLocalizationId, initialMetaForm, initialLocalizationForm, open]);

    const canSave =
        !!localizationId.trim() &&
        Object.values(localizationForm).some(
            (val) => val && val.trim().length > 0,
        );

    return (
        <Modal open={open} onClose={onClose}>
            <Box className={styles.modalBox}>
                <Box sx={{ mb: 3 }}>
                    <Box className={styles.modalHeader}>
                        <Typography
                            variant="h6"
                            className={styles.localizationIdLabel}
                        >
                            Localization ID
                        </Typography>
                        <TextField
                            value={localizationId}
                            onChange={(e) => setLocalizationId(e.target.value)}
                            size="small"
                            className={styles.localizationIdLabel}
                            variant="outlined"
                            fullWidth
                        />
                        <IconButton
                            onClick={onClose}
                            size="small"
                            className={styles.ml1}
                        >
                            <Icon name="close" />
                        </IconButton>
                    </Box>
                    {/* Metadata */}
                    <Box className={styles.metaFields}>
                        <TextField
                            label="Character limit"
                            name="characterLimit"
                            type="number"
                            value={metaForm.characterLimit}
                            onChange={(e) =>
                                setMetaForm((prev) => ({
                                    ...prev,
                                    characterLimit: Number(e.target.value),
                                }))
                            }
                            size="small"
                            className={styles.characterLimit}
                        />
                        <TextField
                            label="Notes"
                            name="notes"
                            value={metaForm.notes}
                            onChange={(e) =>
                                setMetaForm((prev) => ({
                                    ...prev,
                                    notes: e.target.value,
                                }))
                            }
                            fullWidth
                            size="small"
                        />
                    </Box>
                </Box>
                {/* Languages*/}
                <Box className={styles.languageGrid}>
                    {Object.values(LanguageType).map((lang) => (
                        <TextField
                            key={lang}
                            label={lang.toUpperCase()}
                            value={localizationForm[lang] || ''}
                            onChange={(e) =>
                                setLocalizationForm((prev) => ({
                                    ...prev,
                                    [lang]: e.target.value,
                                }))
                            }
                            size="small"
                            fullWidth
                            multiline
                            minRows={2}
                            maxRows={10}
                        />
                    ))}
                </Box>
                {/* Save Button*/}
                <Box className={styles.saveButtonContainer}>
                    <Button
                        onClick={() =>
                            onSave({
                                localizationId,
                                metaForm,
                                localizationForm,
                            })
                        }
                        variant="contained"
                        color="primary"
                        disabled={!canSave}
                    >
                        Save
                    </Button>
                </Box>
            </Box>
        </Modal>
    );
};
